import React from 'react';
import './Process.css';
import { motion } from 'framer-motion';
import { FaSearch, FaLightbulb, FaCode, FaRocket, FaHandsHelping, FaCheckDouble } from 'react-icons/fa';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import useSEO, { SITE_URL } from '../utils/useSEO';

const PROCESS_SCHEMA = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "@id": `${SITE_URL}/process#webpage`,
    "url": `${SITE_URL}/process`,
    "name": "Our Development Process | How We Work | Nimai Solutions",
    "description": "Discover Nimai Solutions' proven development process — from discovery and design to development, testing and launch. Trusted by businesses in Vadodara and across India.",
    "isPartOf": { "@id": `${SITE_URL}/#website` },
    "breadcrumb": {
        "@type": "BreadcrumbList",
        "itemListElement": [
            { "@type": "ListItem", "position": 1, "name": "Home",    "item": SITE_URL },
            { "@type": "ListItem", "position": 2, "name": "Process", "item": `${SITE_URL}/process` }
        ]
    }
};

const steps = [
    {
        id: "01",
        title: "Discovery & Strategy",
        icon: <FaSearch />,
        description: "We dive deep into your business goals, target audience, and market landscape to define a clear roadmap for success.",
        details: ["Stakeholder Interviews", "Competitor Analysis", "Technical Audit", "Project Roadmap"]
    },
    {
        id: "02",
        title: "Design & Experience",
        icon: <FaLightbulb />,
        description: "Focusing on user-centric design, we create intuitive interfaces and engaging experiences that resonate with your brand.",
        details: ["User Flow Mapping", "Wireframing", "High-Fidelity UI Design", "Interactive Prototyping"]
    },
    {
        id: "03",
        title: "Engineering & Dev",
        icon: <FaCode />,
        description: "Our developers build robust, scalable solutions using cutting-edge technologies and agile methodologies.",
        details: ["Architecture Design", "Frontend & Backend Dev", "API Integration", "Weekly Sprints"]
    },
    {
        id: "04",
        title: "Quality Assurance",
        icon: <FaCheckDouble />,
        description: "Rigorous testing ensures your product is bug-free, performant, and secure across all devices and platforms.",
        details: ["Unit & Integration Testing", "Performance Optimization", "Security Audits", "User Acceptance Testing"]
    },
    {
        id: "05",
        title: "Deployment & Launch",
        icon: <FaRocket />,
        description: "We handle the entire deployment process, ensuring a smooth transition to the live environment.",
        details: ["Cloud Infrastructure Setup", "CI/CD Pipeline", "Domain & SSL Config", "Post-Launch Monitoring"]
    },
    {
        id: "06",
        title: "Growth & Support",
        icon: <FaHandsHelping />,
        description: "Our partnership doesn't end at launch. We provide ongoing support and optimizations to help your product grow.",
        details: ["Maintenance & Updates", "Performance Analytics", "Feature Enhancements", "Scale Planning"]
    }
];

const fadeInUp = {
    hidden: { opacity: 0, y: 30 },
    visible: {
        opacity: 1,
        y: 0,
        transition: { duration: 0.6, ease: "easeOut" }
    }
};

const Process = () => {
    useSEO({
        title:       "Our Development Process | How We Work | Nimai Solutions",
        description: "Nimai Solutions follows a 6-step development process: Discovery, Design, Development, Testing, Launch and Support. Trusted by businesses in Vadodara and across India.",
        canonical:   `${SITE_URL}/process`,
        keywords:    "software development process Vadodara, agile development India, web development workflow, app development steps Gujarat",
        schema:      PROCESS_SCHEMA,
    });

    return (
        <div className="process-container">
            <Navbar />

            <section className="process-hero">
                <div className="process-hero-bg"></div>
                <motion.div
                    className="process-hero-content"
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.8 }}
                >
                    <span className="process-badge">Our Methodology</span>
                    <h1>The <span>Blueprint</span> to Delivery.</h1>
                    <p>A systematic, transparent approach to building world-class digital products from concept to scale.</p>
                </motion.div>
            </section>

            <section className="process-steps-section">
                <div className="process-timeline-line"></div>

                <div className="process-steps-grid">
                    {steps.map((step, index) => (
                        <motion.div
                            key={step.id}
                            className={`process-step-item ${index % 2 === 0 ? 'left' : 'right'}`}
                            variants={fadeInUp}
                            initial="hidden"
                            whileInView="visible"
                            viewport={{ once: true, margin: "-100px" }}
                        >
                            <div className="step-number-flow">{step.id}</div>

                            <div className="process-step-card">
                                <div className="step-icon-box" style={{ color: index % 2 === 0 ? 'var(--accent-orange)' : '#3b82f6' }}>
                                    {step.icon}
                                </div>
                                <h2>{step.title}</h2>
                                <p>{step.description}</p>
                                <div className="step-tags">
                                    {step.details.map((detail, i) => (
                                        <span key={i}>{detail}</span>
                                    ))}
                                </div>
                            </div>
                        </motion.div>
                    ))}
                </div>
            </section>

            <section className="process-cta">
                <div className="cta-container">
                    <motion.div
                        className="cta-main-card"
                        initial={{ opacity: 0, scale: 0.95 }}
                        whileInView={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 0.8 }}
                        viewport={{ once: true }}
                    >
                        <div className="cta-blob blob-1"></div>
                        <div className="cta-blob blob-2"></div>

                        <div className="cta-content">
                            <motion.span
                                className="cta-tag"
                                initial={{ opacity: 0, y: 10 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                transition={{ delay: 0.2 }}
                            >
                                Let's Build Something
                            </motion.span>
                            <motion.h2
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                transition={{ delay: 0.3 }}
                            >
                                Ready to bring your <span>vision</span> to life?
                            </motion.h2>
                            <motion.p
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                transition={{ delay: 0.4 }}
                            >
                                Join forces with our team to create a digital product that stands out and scales.
                            </motion.p>
                            <motion.div
                                className="cta-actions"
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                transition={{ delay: 0.5 }}
                            >
                                <button className="cta-primary" onClick={() => window.location.href = '/contact'}>
                                    Book a Strategy Call
                                </button>
                                <button className="cta-secondary" onClick={() => window.location.href = '/portfolio'}>
                                    View Our Work
                                </button>
                            </motion.div>
                        </div>
                    </motion.div>
                </div>
            </section>

            <Footer />
        </div>
    );
};

export default Process;
